/**
 * ReferralsPage (Рефералы) — the referral program screen (DESIGN frame 10).
 *
 * Shows the user's star-glitter balance, a shareable invite link (deep-link into the
 * bot's Mini App), a small stat row and a "how it works" explainer.
 *
 * The glitter balance and the inviter's Telegram id come from ``GET /api/me/`` when
 * available; the per-referral aggregate stats ("invited" / "with purchases") are not yet
 * exposed by the API, so they render as typed MVP-2 placeholders.
 */
import { useState } from "react";

import { CurrencyIcon } from "@melusine/shared-ui";
import { botStartLink, useBackButton } from "@melusine/shared-telegram";
import { useMe } from "@entities/user";
import { useInvitedUsers, type InvitedUser } from "@entities/referrals";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import styles from "./ReferralsPage.module.css";

/** ``«29 июня 2026»`` — short Russian date for joined/purchase timestamps. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface HowStep {
  readonly n: number;
  readonly title: string;
  readonly desc: string;
}

/** «Как это работает» — the four explainer points from дельта §5. */
const HOW_STEPS: readonly HowStep[] = [
  {
    n: 1,
    title: "Друг открыл приложение по вашей реферальной ссылке",
    desc: "Вы получаете награду за приглашение.",
  },
  {
    n: 2,
    title: "Друг приобретает донат",
    desc: "Покупка засчитывается после успешного выполнения заказа.",
  },
  {
    n: 3,
    title: "Вы получаете звёздный блеск с каждой покупки ваших друзей",
    desc: "Начисление за успешные заказы рефералов.",
  },
  {
    n: 4,
    title: "Тратьте звёздный блеск на оплату заказов до 100%",
    desc: "Звёздным блеском можно гасить стоимость товаров.",
  },
];

export function ReferralsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const invitedState = useInvitedUsers();
  const [copied, setCopied] = useState(false);

  useBackButton(() => {
    navigate(routes.home());
  });

  const glitter = me?.balance.star_glitter ?? 0;
  const invited = me?.referrals_count ?? 0;
  const withPurchases = me?.referrals_with_purchases ?? 0;

  // Classic ?start= deep link (not ?startapp=) so the invited user actually STARTs the
  // bot: that opens a chat the bot can DM (notifications) and the /start handler registers
  // the referral and offers the «Открыть магазин» button. Bot username comes from the API.
  const refToken = me ? `ref_${me.telegram_id}` : "ref_…";
  const fullLink = botStartLink(me?.bot_username, refToken);
  const inviteLink = fullLink
    ? fullLink.replace(/^https:\/\//, "")
    : `t.me/…?start=${refToken}`;

  const onCopy = (): void => {
    if (fullLink === null) {
      return;
    }
    void navigator.clipboard
      .writeText(fullLink)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => {
          setCopied(false);
        }, 1600);
      })
      .catch(() => {
        setCopied(false);
      });
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Рефералы</h1>

      <section className={styles.hero}>
        <CurrencyIcon kind="glitter" size={44} />
        <div className={styles.heroBody}>
          <div className={styles.heroLabel}>Звёздный блеск</div>
          <div className={styles.heroValue}>{formatUnits(glitter)}</div>
          <div className={styles.heroCaption}>Заработано за приглашения</div>
        </div>
      </section>

      <section className={styles.linkCard}>
        <div className={styles.eyebrow}>Ваша ссылка</div>
        <div className={styles.linkRow}>
          <code className={styles.link}>{inviteLink}</code>
          <button
            type="button"
            className={styles.copyButton}
            onClick={onCopy}
            aria-label={copied ? "Ссылка скопирована" : "Скопировать ссылку"}
          >
            {copied ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="9" y="9" width="12" height="12" rx="2" />
                <path d="M5 15V5a2 2 0 0 1 2-2h10" />
              </svg>
            )}
          </button>
        </div>
      </section>

      <section className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statValue}>{formatUnits(invited)}</div>
          <div className={styles.statLabel}>Приглашено</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>{formatUnits(withPurchases)}</div>
          <div className={styles.statLabel}>С покупками</div>
        </div>
        <div className={styles.stat}>
          <div className={`${styles.statValue} ${styles.statGlitter}`}>
            {formatUnits(glitter)}
          </div>
          <div className={styles.statLabel}>Блеска</div>
        </div>
      </section>

      <section className={styles.invited}>
        <div className={styles.eyebrow}>Приглашённые</div>
        <InvitedList state={invitedState} />
      </section>

      <section className={styles.how}>
        <div className={styles.eyebrow}>Как это работает</div>
        {HOW_STEPS.map((step) => (
          <div key={step.n} className={styles.step}>
            <span className={styles.stepNum}>{step.n}</span>
            <span className={styles.stepBody}>
              <span className={styles.stepTitle}>{step.title}</span>
              <span className={styles.stepDesc}>{step.desc}</span>
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}

interface InvitedListProps {
  readonly state: ReturnType<typeof useInvitedUsers>;
}

function InvitedList({ state }: InvitedListProps): JSX.Element {
  const { data, isLoading, error } = state;

  if (isLoading) {
    return <p className={styles.invitedEmpty}>Загружаем список…</p>;
  }
  if (error) {
    return (
      <p className={styles.invitedEmpty}>
        Не удалось загрузить список приглашённых.
      </p>
    );
  }
  if (data === null || data.length === 0) {
    return (
      <p className={styles.invitedEmpty}>
        Вы пока никого не пригласили. Поделитесь ссылкой выше.
      </p>
    );
  }

  return (
    <ul className={styles.invitedList}>
      {data.map((user) => (
        <InvitedRow key={user.id} user={user} />
      ))}
    </ul>
  );
}

interface InvitedRowProps {
  readonly user: InvitedUser;
}

function InvitedRow({ user }: InvitedRowProps): JSX.Element {
  const name =
    user.display_name || (user.username ? `@${user.username}` : "Пользователь");
  return (
    <li className={styles.invitedItem}>
      <div className={styles.invitedHead}>
        <span className={styles.invitedName}>{name}</span>
        {user.glitter_earned > 0 ? (
          <span className={styles.invitedGlitter}>
            <CurrencyIcon kind="glitter" size={14} />+
            {formatUnits(user.glitter_earned)}
          </span>
        ) : null}
      </div>
      <div className={styles.invitedMeta}>
        Присоединился {formatDate(user.joined_at)}
        {user.has_purchases ? ` · покупок: ${formatUnits(user.purchases_count)}` : ""}
      </div>
      {user.has_purchases && user.products.length > 0 ? (
        <ul className={styles.invitedProducts}>
          {user.products.map((product, index) => (
            <li
              key={`${product.title}-${product.purchased_at}-${index}`}
              className={styles.invitedProduct}
            >
              <span className={styles.invitedProductTitle}>
                {product.title}
                {product.qty > 1 ? ` ×${product.qty}` : ""}
              </span>
              <span className={styles.invitedProductDate}>
                {formatDate(product.purchased_at)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.invitedNoPurchases}>Покупок пока нет</div>
      )}
    </li>
  );
}
